/**
 * Custom DateField component that ensures proper UTC to local timezone conversion
 * This component wraps React Admin's DateField with timezone-aware formatting
 * 
 * Backend returns dates in UTC ISO 8601 format (with 'Z' suffix)
 * This component automatically converts to user's local timezone for display
 */

import React from 'react';
import { useRecordContext } from 'react-admin';
import { formatUTCToLocal } from '../utils/dateTimezone.util';

interface LocalizedDateFieldProps {
  source: string;
  label?: string;
  showTime?: boolean;
  options?: Intl.DateTimeFormatOptions;
  emptyText?: string;
}

/**
 * DateField component for displaying UTC dates in local timezone
 * 
 * @param props - Standard DateField props plus showTime and options
 * @returns Rendered date field with local timezone conversion
 * 
 * @example
 * // Display date only (default)
 * <LocalizedDateField source="createdAt" label="Created" />
 * 
 * // Display date and time
 * <LocalizedDateField source="createdAt" label="Created" showTime={true} />
 * 
 * // Custom formatting
 * <LocalizedDateField 
 *   source="createdAt" 
 *   label="Created"
 *   options={{ year: 'numeric', month: 'long', day: 'numeric' }}
 * />
 */
export const LocalizedDateField: React.FC<LocalizedDateFieldProps> = ({
  source,
  showTime = true,
  options,
  emptyText = '-',
}) => {
  const record = useRecordContext();
  
  if (!record) {
    return <span>{emptyText}</span>;
  }
  
  const isoString = record[source];
  
  if (!isoString) {
    return <span>{emptyText}</span>;
  }
  
  // Determine formatting options
  let formatOptions = options;
  if (!formatOptions) {
    formatOptions = showTime
      ? {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        }
      : {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        };
  }
  
  const formattedDate = formatUTCToLocal(isoString, formatOptions);
  
  return <span>{formattedDate}</span>;
};

/**
 * Shorter alias for LocalizedDateField
 * @example <LocalDate source="createdAt" label="Created" />
 */
export const LocalDate = LocalizedDateField;

export default LocalizedDateField;
